"use client";

import { useEffect, useRef, useState } from "react";
import { EditorContent, EditorContext, useEditor } from "@tiptap/react";
import type { Editor } from "@tiptap/core";

// --- Tiptap Core Extensions ---
import { StarterKit } from "@tiptap/starter-kit";
import { Image } from "@tiptap/extension-image";
import { TaskItem, TaskList } from "@tiptap/extension-list";
import { TextAlign } from "@tiptap/extension-text-align";
import { Typography } from "@tiptap/extension-typography";
import { Highlight } from "@tiptap/extension-highlight";
import { Subscript } from "@tiptap/extension-subscript";
import { Superscript } from "@tiptap/extension-superscript";
import { Selection } from "@tiptap/extensions";
import { CharacterCount } from "@tiptap/extension-character-count";

// --- UI Primitives ---
import { Button } from "../../tiptap-ui-primitive/button";
// import { Spacer } from "../../tiptap-ui-primitive/spacer";
import { Toolbar, ToolbarGroup, ToolbarSeparator } from "../../tiptap-ui-primitive/toolbar";

// --- Tiptap Node ---
import { ImageUploadNode } from "../../tiptap-node/image-upload-node/image-upload-node-extension";
import { ImagePasteDrop } from "../../tiptap-node/image-upload-node/image-paste-drop-extension";
import { HorizontalRule } from "../../tiptap-node/horizontal-rule-node/horizontal-rule-node-extension";
import "../../tiptap-node/blockquote-node/blockquote-node.css";
import "../../tiptap-node/code-block-node/code-block-node.css";
import "../../tiptap-node/horizontal-rule-node/horizontal-rule-node.css";
import "../../tiptap-node/list-node/list-node.css";
import "../../tiptap-node/image-node/image-node.css";
import "../../tiptap-node/heading-node/heading-node.css";
import "../../tiptap-node/paragraph-node/paragraph-node.css";

// --- Tiptap UI ---
import { HeadingDropdownMenu } from "../../tiptap-ui/heading-dropdown-menu";
import { ImageUploadButton } from "../../tiptap-ui/image-upload-button";
import { ListDropdownMenu } from "../../tiptap-ui/list-dropdown-menu";
import { BlockquoteButton } from "../../tiptap-ui/blockquote-button";
// import { CodeBlockButton } from "../../tiptap-ui/code-block-button";
import {
  // ColorHighlightPopover,
  ColorHighlightPopoverContent,
  // ColorHighlightPopoverButton,
} from "../../tiptap-ui/color-highlight-popover";
import { LinkContent } from "../../tiptap-ui/link-popover";
import { MarkButton } from "../../tiptap-ui/mark-button";
import { TextAlignButton } from "../../tiptap-ui/text-align-button";
import { UndoRedoButton } from "../../tiptap-ui/undo-redo-button";

// --- Icons ---
import { ArrowLeftIcon } from "../../tiptap-icons/arrow-left-icon";
import { HighlighterIcon } from "../../tiptap-icons/highlighter-icon";
import { LinkIcon } from "../../tiptap-icons/link-icon";

// --- Hooks ---
import { useIsBreakpoint } from "../../../hooks/use-is-breakpoint";
import { useWindowSize } from "../../../hooks/use-window-size";
import { useCursorVisibility } from "../../../hooks/use-cursor-visibility";

// --- Components ---
// import { ThemeToggle } from "../../tiptap-templates/simple/theme-toggle";

// --- Lib ---
import { handleImageUpload, handleImageUploadWithType, MAX_FILE_SIZE } from "../../../lib/tiptap-utils";

// --- Styles ---
import "../../tiptap-templates/simple/controlled-simple-editor.css";

interface SimpleEditorProps {
  value?: string;
  onChange?: (value: string) => void;
  editable?: boolean;
  showToolbar?: boolean;
  maxLength?: number;
  showCharCount?: boolean;
  allowImageUpload?: boolean;
  height?: number | string;
  maxHeight?: number | string;
  uploadType?: string;
}

const MainToolbarContent = ({
  onHighlighterClick,
  onLinkClick,
  isMobile,
  allowImageUpload,
}: {
  onHighlighterClick: () => void;
  onLinkClick: () => void;
  isMobile: boolean;
  allowImageUpload: boolean;
}) => {
  return (
    <>
      {/* <Spacer /> */}

      <ToolbarGroup>
        <UndoRedoButton action="undo" />
        <UndoRedoButton action="redo" />
      </ToolbarGroup>

      <ToolbarSeparator />

      <ToolbarGroup>
        <HeadingDropdownMenu levels={[1, 2, 3, 4]} portal={isMobile} hideWhenUnavailable={true} />
        <ListDropdownMenu types={["bulletList", "orderedList", "taskList"]} portal={isMobile} />
        <BlockquoteButton />
        {/* <CodeBlockButton /> */}
      </ToolbarGroup>

      <ToolbarSeparator />

      <ToolbarGroup>
        <MarkButton type="bold" />
        <MarkButton type="italic" />
        <MarkButton type="strike" />
        <MarkButton type="code" />
        <MarkButton type="underline" />
        {/* {!isMobile ? <ColorHighlightPopover /> : <ColorHighlightPopoverButton onClick={onHighlighterClick} />}
        {!isMobile ? <LinkPopover /> : <LinkButton onClick={onLinkClick} />} */}
      </ToolbarGroup>

      {/* <ToolbarSeparator />

      <ToolbarGroup>
        <MarkButton type="superscript" />
        <MarkButton type="subscript" />
      </ToolbarGroup> */}

      <ToolbarSeparator />

      <ToolbarGroup>
        <TextAlignButton align="left" />
        <TextAlignButton align="center" />
        <TextAlignButton align="right" />
        <TextAlignButton align="justify" />
      </ToolbarGroup>

      {allowImageUpload && (
        <>
          <ToolbarSeparator />
          <ToolbarGroup>
            <ImageUploadButton text="Add" />
          </ToolbarGroup>
        </>
      )}

      {/* <Spacer />

      {isMobile && <ToolbarSeparator />}

      <ToolbarGroup>
        <ThemeToggle />
      </ToolbarGroup> */}
    </>
  );
};

const MobileToolbarContent = ({ type, onBack }: { type: "highlighter" | "link"; onBack: () => void }) => (
  <>
    <ToolbarGroup>
      <Button data-style="ghost" onClick={onBack}>
        <ArrowLeftIcon className="tiptap-button-icon" />
        {type === "highlighter" ? (
          <HighlighterIcon className="tiptap-button-icon" />
        ) : (
          <LinkIcon className="tiptap-button-icon" />
        )}
      </Button>
    </ToolbarGroup>

    <ToolbarSeparator />

    {type === "highlighter" ? <ColorHighlightPopoverContent /> : <LinkContent />}
  </>
);

export function SimpleEditor({
  value = "",
  onChange,
  editable = true,
  showToolbar = true,
  maxLength,
  showCharCount = false,
  allowImageUpload = false,
  height,
  maxHeight,
  uploadType,
}: SimpleEditorProps) {
  const [, forceUpdate] = useState(0);
  const isInternalChangeRef = useRef(false);
  const isMobile = useIsBreakpoint();
  const { height: windowHeight } = useWindowSize();
  const [mobileView, setMobileView] = useState<"main" | "highlighter" | "link">("main");
  const toolbarRef = useRef<HTMLDivElement>(null);

  const editor = useEditor({
    immediatelyRender: false,
    editable: editable,
    editorProps: {
      attributes: {
        autocomplete: "off",
        autocorrect: "off",
        autocapitalize: "off",
        "aria-label": "Main content area, start typing to enter text.",
        class: "simple-editor",
      },
    },
    extensions: [
      StarterKit.configure({
        horizontalRule: false,
        link: {
          openOnClick: false,
          enableClickSelection: true,
        },
      }),
      HorizontalRule,
      TextAlign.configure({ types: ["heading", "paragraph"] }),
      TaskList,
      TaskItem.configure({ nested: true }),
      Highlight.configure({ multicolor: true }),
      Image.configure({
        inline: true,
        allowBase64: true,
      }),
      Typography,
      Superscript,
      Subscript,
      Selection,
      ImageUploadNode.configure({
        accept: "image/*",
        maxSize: MAX_FILE_SIZE,
        limit: 3,
        upload: uploadType ? handleImageUploadWithType(uploadType) : handleImageUpload,
        onError: (error) => console.error("Upload failed:", error),
      }),
      ...(allowImageUpload
        ? [
            ImagePasteDrop.configure({
              accept: "image/*",
              maxSize: MAX_FILE_SIZE,
              limit: 3,
              onError: (error) => console.error("Paste/Drop rejected:", error),
            }),
          ]
        : []),
      ...(maxLength ? [CharacterCount.configure({ limit: maxLength })] : []),
    ],
    content: value,
    onUpdate: ({ editor }: { editor: Editor }) => {
      isInternalChangeRef.current = true;
      const html = editor.getHTML();
      if (onChange) {
        onChange(html);
      }
      forceUpdate((n) => n + 1);
    },
  });

  const rect = useCursorVisibility({
    editor,
    overlayHeight: toolbarRef.current?.getBoundingClientRect().height ?? 0,
  });

  useEffect(() => {
    if (isInternalChangeRef.current) {
      isInternalChangeRef.current = false;
      return;
    }
    if (editor && value !== editor.getHTML()) {
      editor.commands.setContent(value, { emitUpdate: false });
    }
  }, [value, editor]);

  useEffect(() => {
    if (!isMobile && mobileView !== "main") {
      setMobileView("main");
    }
  }, [isMobile, mobileView]);

  return (
    <div
      className={`simple-editor-wrapper flex flex-col w-full${editable ? "" : " simple-editor-wrapper--readonly"}`}
      style={height ? { height } : {}}
    >
      <EditorContext.Provider value={{ editor }}>
        {showToolbar && (
          <Toolbar
            ref={toolbarRef}
            style={{
              ...(isMobile
                ? {
                    bottom: `calc(100% - ${windowHeight - rect.y}px)`,
                  }
                : {}),
            }}
          >
            {mobileView === "main" ? (
              <MainToolbarContent
                allowImageUpload={allowImageUpload}
                onHighlighterClick={() => setMobileView("highlighter")}
                onLinkClick={() => setMobileView("link")}
                isMobile={isMobile}
              />
            ) : (
              <MobileToolbarContent
                type={mobileView === "highlighter" ? "highlighter" : "link"}
                onBack={() => setMobileView("main")}
              />
            )}
          </Toolbar>
        )}

        <EditorContent
          editor={editor}
          role="presentation"
          className="simple-editor-content"
          style={
            maxHeight !== undefined
              ? ({ "--simple-editor-max-height": typeof maxHeight === "number" ? `${maxHeight / 16}rem` : maxHeight } as React.CSSProperties)
              : undefined
          }
        />
        {showCharCount && maxLength && editor && (
          <div className="character-count">
            {editor.storage.characterCount.characters()}/{maxLength}
          </div>
        )}
      </EditorContext.Provider>
    </div>
  );
}
